import styled from 'styled-components';

import {
  ANIM,
  COLORS,
  FIXED_HEADER,
  GRADIENTS,
  MOBILE,
  MOBILE_NAV_ANIM,
  REDUCED_MOTION,
  STATUS_COLORS,
} from '../../theme/tokens';
import { MOBILE_NAV_HIDDEN } from '../../layouts/MainLayout/mobileNavVisibility';
import { Button } from '../../components/Button/Button';
import { Text } from '../../components/Text/Text';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* Clears the sticky action bar, so the last field is never parked underneath it. */
  padding: 2px 2px 96px;
`;

/** Fields inside a panel stack with a comfortable rhythm. */
export const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/** Panel heading row: the section title with its own actions pushed to the right. */
export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

/**
 * Anything the generating wash covers has to be its positioned parent and its
 * own stacking context, or the overlay resolves against the page and covers the
 * panel instead of the field. Used around each SEO input and around the body
 * editor.
 */
export const WashHost = styled.div`
  position: relative;
  isolation: isolate;
  border-radius: 12px;
`;

/** Two side-by-side fields (category/language); stacks on mobile. */
export const SplitRow = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
    min-width: 0;
  }

  ${MOBILE} {
    flex-direction: column;
  }
`;

/** Status chip + its kebab of publish/delete actions. */
export const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: -4px;
`;

/** The two icon-only header actions (preview, settings). */
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/**
 * The generated slug, sitting under the Title input it is derived from. Monospaced
 * digits keep the id suffix from jittering as the title is typed, and the whole
 * line truncates rather than wrapping the form open.
 */
export const SlugCaption = styled(Text)`
  display: block;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textFaint};
  margin-top: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ErrorHint = styled(Text)`
  display: block;
  font-size: 13px;
  color: ${STATUS_COLORS.negative};
  margin-top: 6px;
`;

/**
 * How far above the bottom of the screen the bar rests where the mobile bottom
 * navigation is shown (below NAV_DESKTOP, i.e. the `FIXED_HEADER` regime, which
 * is wider than the area's `MOBILE`). The same clearance `PageShell` pads every
 * page with on that regime (its `MOBILE_NAV_PX`: the pill's ~104px plus a
 * buffer), so the bar sits clear of the pill, and the place it is pinned to is
 * exactly where it comes to rest at the end of the scroll: no jump. (The pill
 * always shows near the end of the page, so the bar is lifted there too.)
 */
const MOBILE_NAV_CLEARANCE_PX = 134;

/**
 * Save (and Publish, for a saved draft), pinned to the bottom of the viewport.
 *
 * The article form is long enough that the end of it is several screens from the
 * top, so the bar stays in view and saving or publishing never costs a scroll to
 * the bottom and back. Its height (20px + a 48px button + 24px) stays inside the
 * 96px the Section reserves for it; the bottom pad is the larger one so the
 * buttons do not sit tight against the bottom edge of the content card.
 *
 * That height holds only on ONE row, so the bar never wraps: where the two
 * buttons do not fit whole (the French labels on a 320px phone), Save gives way
 * instead (see `FooterSave`).
 *
 * Below 900px it rests above the mobile bottom navigation while that shows, and
 * follows it down to the bottom edge while it is scrolled away.
 */
export const FooterBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  margin: 2px -2px -96px;
  padding: 20px 2px calc(24px + env(safe-area-inset-bottom));
  background: ${GRADIENTS.footerScrim};

  ${FIXED_HEADER} {
    /* Above the fixed bottom navigation, which would otherwise cover the bar
       whenever it slides back in. The safe area is in the offset now, so it
       leaves the padding. */
    bottom: calc(${MOBILE_NAV_CLEARANCE_PX}px + env(safe-area-inset-bottom));
    padding-bottom: 24px;
    /* Moves with the pill: the same duration and curve as its hide-on-scroll
       slide, so the two travel together. */
    transition:
      bottom ${MOBILE_NAV_ANIM.hideMs}ms ${ANIM.EASE},
      padding-bottom ${MOBILE_NAV_ANIM.hideMs}ms ${ANIM.EASE};

    /* While the navigation has slid away (scrolling down the form), the bar
       follows it down to the bottom edge, as on desktop, instead of leaving a
       band of form showing below it. */
    ${MOBILE_NAV_HIDDEN} & {
      bottom: 0;
      padding-bottom: calc(24px + env(safe-area-inset-bottom));
    }

    ${REDUCED_MOTION} {
      transition: none;
    }
  }
`;

/**
 * Save in the bottom bar. Where it and Publish do not fit whole on the one row,
 * Save is the button that shrinks, and its label ends in an ellipsis rather
 * than wrapping. Only the drawn text is cut: the label stays whole in the DOM,
 * so the button's accessible name is the full label.
 */
export const FooterSave = styled(Button)`
  flex: 0 1 auto;
  min-width: 0;

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** Publish in the bottom bar: it never shrinks, so the action that goes live always reads whole. */
export const FooterPublish = styled(Button)`
  flex: none;
`;

/** Warning bullets inside the publish confirm modal. */
export const WarnList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const WarnItem = styled(Text)`
  display: list-item;
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

export const ModalCopy = styled(Text)`
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** Centred retry action under the load-failure message. */
export const RetryWrap = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 8px;
`;

/** Loading skeleton column while the article loads. */
export const LoadingStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/** Chip row of the related-articles picker. */
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const GroupLabel = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
